import './article.css';
import './reader.css';
import './_toc.css';
import './_app-menu.css';
import './_toc_biome-skip.css';
import './_cards.css';
import './_image-modal.css';
import './_overview.css';
import './_reader-article.css';
import './_deep-dive.css';
import './reader__amedia.css';
import './reader__dn.css';
import './reader__nordjyske.css';
import './reader__schibsted.css';
import {renderAppMenu} from './_app-menu.ts';
import type {CardLayout, ItemStateValue} from '../../../types/types.ts';
import {renderReaderSkeleton} from '../../components/reader-skeleton.ts';
import {FADE_IN, FADE_OUT, triggerAnimation} from '../../lib/animations.ts';
import {createElement} from '../../lib/dom.ts';
import {getClientId} from '../../lib/storage.ts';
import {Store} from '../../lib/store.ts';
import {Article} from '../../models/article.ts';
import type {ReaderEdition, ReaderItem} from '../../models/reader-edition.ts';
import {renderToc} from './_toc.ts';
import {renderEndPage} from './end-page.ts';
import {renderFrontPage} from './front-page.ts';
import {getAvailableLayoutsForCard} from './lib/card-layouts.ts';
import {EVENT_CARD_ACTIVATED} from './lib/cards.ts';
import {Listings} from './lib/listings.ts';
import {ReaderListing} from './reader-listing.ts';

type ReaderAction =
	| 'ai-rewrite'
	| 'toggle-overview'
	| 'close-dialogs'
	| 'toggle-menu'
	| 'toggle-deep-dive'
	| 'show-overview'
	| 'hide-overview'
	| 'set-toc-placement'
	| 'set-design'
	| 'set-mode'
	| 'set-font'
	| 'set-navigation-model'
	| 'front-page'
	| 'end-page'
	| 'go-to-article'
	| 'go-to-permalink'
	| 'show-toc'
	| 'next-article'
	| 'previous-article'
	| 'next-card'
	| 'previous-card'
	| 'activate-card'
	| 'set-card-layout'
	| 'redraw-article'
	| 'done'
	| 'star'
	| 'toggle-show-done'
	| 'toggle-layout-menu'
	| 'reset-state'
	| 'open-source-url'
	| 'open-in-lp'
	| 'open-admin';

type PageType = 'front-page' | 'article' | 'end-page' | 'overview' | 'toc';
type PointerType = 'mouse' | 'pen' | 'touch';

const hotkeys: Record<string, ReaderAction> = {
	' ': 'toggle-overview',
	ArrowUp: 'previous-article',
	ArrowDown: 'next-article',
	k: 'previous-card',
	ArrowLeft: 'previous-card',
	j: 'next-card',
	h: 'toggle-show-done',
	m: 'toggle-menu',
	l: 'toggle-layout-menu',
	b: 'toggle-deep-dive',
	p: 'go-to-permalink',
	n: 'set-design',
	f: 'set-mode',
	v: 'set-font',
	ArrowRight: 'next-card',
	r: 'redraw-article',
	a: 'ai-rewrite',
	Escape: 'close-dialogs'
};

export async function renderReaderPage(
	appEl: HTMLElement,
	readerEdition: ReaderEdition,
	resourceType: 'reader' | 'feed' | 'digital-reader' | 'print-feed' | 'article' | 'reels' | 'pressflow',
	subResource: string | null,
	pageNumber: number
) {
	appEl.dataset.productName = readerEdition.productName;
	appEl.dataset.productType = resourceType;
	appEl.dataset.editionType = readerEdition.productType;

	appEl.innerHTML = `
${renderReaderSkeleton()}
`;

	const store = await Store.load();
	const reverseSortOrder = ['feed', 'reels'].includes(resourceType);
	let tocPlacement = store.getUserPreference('toc-placement') || 'below';
	let design = store.getUserPreference('design') || 'gunnar';
	let mode = store.getUserPreference('mode') || 'pressflow';
	let font = store.getUserPreference('font') || 'serif';
	let navigationModel = store.getUserPreference('navigation-model') || 'horizontal-scroll';
	let showDone = store.getUserPreference('show-done') || 'show';
	const hasStarredEdition = store.hasStarredResource(readerEdition.id);
	appEl.dataset.hasStarredEdition = hasStarredEdition ? 'on' : 'off';
	appEl.dataset.tocPlacement = tocPlacement;
	appEl.dataset.design = design;
	appEl.dataset.mode = mode;
	appEl.dataset.font = font;
	appEl.dataset.navigationModel = navigationModel;
	const appMenuEl = appEl.querySelector('#app-menu') as HTMLElement;
	const articleViewEl = appEl.querySelector('#article-view') as HTMLElement;
	const overViewEl = appEl.querySelector('#overview') as HTMLElement;
	const tocListEl = appEl.querySelector('#toc') as HTMLElement;
	appEl.dataset.done = showDone;
	let frontPageEl: HTMLElement;
	const endPageEl: HTMLElement = createElement(appEl.ownerDocument, 'div', 'end-page-card');

	const loadItemPromises: Promise<void>[] = [];
	const articles = await loadArticleList();
	if (!articles) {
		articleViewEl.innerHTML =
			'<div class="error">Error loading articles.<br/> <button class="button" type="button">Reload page</button></div>';
		articleViewEl.querySelector('button')?.addEventListener('click', () => window.location.reload());
		return;
	}

	const mobileMode = window.matchMedia('(width <= 900px)');
	const narrowScreenMode = window.matchMedia('(width < 450px)');
	const tabletMode = window.matchMedia('(hover: none) and (width >= 1024px)');
	const desktopMode = window.matchMedia('(hover: hover) and (width > 900px)');
	const wideScreenMode = window.matchMedia('(width >= 1680px)');
	const listings = createListings(articles);
	const intersectionObserver = createIntersectionObserver();

	let activeListing: ReaderListing | null;
	let activeTocListing: ReaderListing | null;
	let activePageType: PageType;
	let inlineScroll: ScrollLogicalPosition | undefined = 'start';
	setInlineScroll();
	appEl.dataset.status = 'loaded';
	let isScrollingIntoView = false;
	let isPostProcessingPending = true;
	let hasUserScrolled = false;

	// Helper to check if page-navigation mode is active
	const isPageNavMode = () => navigationModel === 'page-navigation' && narrowScreenMode.matches;

	renderToc(tocListEl, listings, readerEdition);
	renderAppMenu(appMenuEl, readerEdition, resourceType, {mode, navigationModel, design, font, showDone});
	if (resourceType !== 'article') {
		renderEndPage(endPageEl, readerEdition);
	}

	await ensureFontsLoaded(appEl);
	render(mobileMode.matches, tabletMode.matches);
	await activateResource(subResource, null);
	if (mobileMode.matches || tabletMode.matches) {
		activateIntersectionObserver();
	}

	initEventListeners();

	await Promise.all(loadItemPromises);
	console.time('postprocess');
	for (const listing of listings.toArray()) {
		await new Promise((resolve) => setTimeout(resolve, 0)); // Yield to event loop
		listing.postProcessCards(mode);
	}
	isPostProcessingPending = false;
	console.timeEnd('postprocess');

	async function loadArticleList() {
		try {
			return await readerEdition.loadArticleList(store);
		} catch (err) {
			console.log(err);
			return null;
		}
	}

	function setInlineScroll() {
		if (tabletMode.matches) {
			inlineScroll = 'start';
			return;
		}

		if (mobileMode.matches) {
			inlineScroll = 'start';
			return;
		}

		inlineScroll = undefined;
	}

	function render(isMobileMode: boolean, isTabletMode: boolean) {
		renderItems(listings);
		articleViewEl.innerHTML = '';
		if (resourceType !== 'article') {
			frontPageEl = createElement(appEl.ownerDocument, 'div', 'front-page');
			renderFrontPage(frontPageEl, readerEdition);
		}

		if (isMobileMode || isTabletMode) {
			listings.forEach((listing: ReaderListing) => {
				listing.addToCurrentViewEl();
			});
			if (resourceType !== 'article') {
				articleViewEl.appendChild(endPageEl);
			}
		}
	}

	function activateIntersectionObserver() {
		listings.forEach((listing: ReaderListing) => {
			intersectionObserver.observe(listing.el);
		});
	}

	async function activateResource(subResource: string | null, listing: ReaderListing | null) {
		if (resourceType === 'article') {
			const articleToActivate = listings.at(0);
			if (articleToActivate) {
				// Ensure article is rendered before activating
				await articleToActivate.renderPromise;
				setActiveListing(articleToActivate, pageNumber);
			}
			return;
		}

		if (listing) {
			await listing.currentArticleMode(mode);
			setActiveListing(listing, pageNumber);
			listing.activateCard(1);
			return;
		}

		if (subResource) {
			switch (subResource) {
				case 'overview':
					showOverview();
					return;
				case 'end-page':
					goToEndPage('instant');
					return;
				default: {
					const listing = listings.get(subResource);
					if (listing) {
						await listing.renderPromise;
						isScrollingIntoView = true;
						articleViewEl.scrollIntoView({
							block: 'start',
							inline: inlineScroll,
							behavior: 'smooth'
						});

						listing.activateCard(pageNumber);
						setActiveListing(listing, pageNumber);
						return;
					}
				}
			}
		}

		if (activeListing) {
			activeListing.activateCard(1);
			return;
		}

		if (mobileMode.matches) {
			// In page-navigation mode, show TOC initially
			if (isPageNavMode()) {
				// tocListEl.style.display = 'flex';
				// articleViewEl.style.display = 'none';
				setActivePageType('toc');
			}
			const state = {pageType: 'toc', id: null};
			// TODO: Should this be replace? Sometimes?
			history.pushState(state, '', readerEdition.getPath() + window.location.search);
		} else {
			goToFrontPage();
		}
	}

	function updateUrl(pageType = activePageType) {
		if (resourceType === 'pressflow') {
			return;
		}
		let path = readerEdition.getPath();
		let search = window.location.search;

		switch (pageType) {
			case 'front-page':
				// path is already correct
				break;
			case 'end-page':
				path = `${path}/end-page`;
				break;
			case 'article': {
				if (activeListing) {
					path = readerEdition.getArticleUrl(activeListing, activeListing.getCurrentCardNumber());
				}
				break;
			}
			case 'overview':
				path = `${path}/overview`;
				break;
		}

		const [pathname, queryString] = path.split('?');
		if (queryString) {
			search = `?${queryString}`;
		}
		const newUrl = pathname + search;

		if (window.location.href === newUrl) {
			return;
		}

		const state = {pageType: pageType, id: activeListing?.item.id};

		// Push when entering article view from non-article state, replace when already in articles
		const shouldPush = history.state?.pageType !== 'article' && pageType === 'article';

		if (shouldPush) {
			history.pushState(state, '', newUrl);
		} else {
			history.replaceState(state, '', newUrl);
		}
	}

	function createIntersectionObserver() {
		return new IntersectionObserver(
			(entries: IntersectionObserverEntry[], _observer: IntersectionObserver) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (isScrollingIntoView || isPostProcessingPending) {
							return;
						}
						const target = entry.target as HTMLElement;
						if (target.classList.contains('front-page')) {
							activateFrontPage();
							return;
						}
						const listing = listings.get(target.dataset.id);
						if (listing && !listing.isActive) {
							// In page-navigation mode, always update URL; in horizontal-scroll mode, delay first update until user scrolls
							const skipUrlUpdate =
								!isPageNavMode() &&
								narrowScreenMode.matches &&
								listings.getIndexOf(listing) === 0 &&
								!hasUserScrolled;
							setActiveListing(listing, listing.getCurrentCardNumber() || 1, true, skipUrlUpdate);
						}
					}
				});
			},
			{
				root: articleViewEl,
				rootMargin: '0% 0% 0% 0%',
				threshold: 0.9
			}
		);
	}

	function isAtEditionRoot() {
		return window.location.pathname === readerEdition.getPath();
	}

	function initEventListeners() {
		// Track user scrolling on narrow screens to delay URL update
		let scrollTimeout: ReturnType<typeof setTimeout>;
		const handleScroll = () => {
			if (narrowScreenMode.matches && !hasUserScrolled) {
				clearTimeout(scrollTimeout);
				scrollTimeout = setTimeout(() => {
					hasUserScrolled = true;
					if (activeListing && listings.getIndexOf(activeListing) === 0 && isAtEditionRoot()) {
						updateUrl();
					}
					appEl.removeEventListener('scroll', handleScroll);
				}, 150);
			}
		};

		appEl.addEventListener('scroll', handleScroll);

		const resizeObserver = new ResizeObserver(() => {
			if (activeListing) {
				activeListing.redrawCards(mode);
			}
		});
		resizeObserver.observe(window.document.documentElement);

		addEventListener('popstate', (event: PopStateEvent) => {
			// In page-navigation mode, back button from article goes to TOC
			if (isPageNavMode() && event.state?.pageType === 'toc' && activePageType === 'article') {
				withViewTransition(() => showTocPage());
				return;
			}

			// Original horizontal-scroll behavior. This does not work reliably.
			if (narrowScreenMode.matches) {
				const tocRect = tocListEl.getBoundingClientRect();
				const isTocVisible = tocRect.left >= 0;

				if (!isTocVisible) {
					event.preventDefault();
					history.pushState(history.state, '');
					tocListEl.scrollIntoView({block: 'start', inline: 'start', behavior: 'smooth'});
					return;
				}

				if (isTocVisible && isAtEditionRoot() && !event.state) {
					history.back();
					return;
				}
			}

			if (!event.state) {
				return;
			}

			switch (event.state.pageType) {
				case 'front-page':
					if (activePageType === 'front-page') {
						return;
					}
					goToFrontPage(true);
					break;
				case 'article': {
					const listing = listings.get(event.state.id);
					if (listing) {
						setActiveListing(listing, pageNumber, false, true);
						listing.activateCard(pageNumber);

						requestAnimationFrame(() => {
							articleViewEl.scrollIntoView({
								block: 'start',
								inline: inlineScroll,
								behavior: 'smooth'
							});
						});
					}
					break;
				}
			}
		});
		mobileMode.addEventListener('change', () => {
			if (mobileMode.matches) {
				setInlineScroll();
				render(mobileMode.matches, tabletMode.matches);
				activateResource(subResource, activeListing);
				activateIntersectionObserver();
			}
		});

		tabletMode.addEventListener('change', () => {
			if (tabletMode.matches) {
				setInlineScroll();
				render(mobileMode.matches, tabletMode.matches);
				activateResource(subResource, activeListing);
				activateIntersectionObserver();
			}
		});

		desktopMode.addEventListener('change', () => {
			if (desktopMode.matches) {
				intersectionObserver.disconnect();
				setInlineScroll();
				render(mobileMode.matches, tabletMode.matches);
				activateResource(subResource, activeListing);
			}
		});

		if ('onscrollend' in articleViewEl) {
			articleViewEl.addEventListener('scrollend', () => {
				isScrollingIntoView = false;
			});
		} else {
			// Safari fallback: use scroll event with debouncing
			let scrollTimeout: ReturnType<typeof setTimeout>;
			// @ts-expect-error Safari does not have onscrollend
			articleViewEl.addEventListener('scroll', () => {
				clearTimeout(scrollTimeout);
				scrollTimeout = setTimeout(() => {
					isScrollingIntoView = false;
				}, 50);
			});
		}

		articleViewEl.addEventListener(EVENT_CARD_ACTIVATED, (event: Event) => {
			const customEvent = event as CustomEvent<{
				cardNumber: number;
				totalCards: number;
				articleId: string;
			}>;

			if (activeListing && activeListing?.id === customEvent.detail.articleId) {
				updateUrl();
				handleProgressUpdate(activeListing);
				activeListing.updateLayoutMenuPopover();
			}
		});

		appEl.addEventListener('click', (event) => {
			const clickTarget = event.target as HTMLElement;
			const actionButton = clickTarget.closest('[data-action]') as HTMLElement;
			if (!actionButton) {
				return;
			}

			event.stopImmediatePropagation();
			// Don't preventDefault for form elements (radio, checkbox, etc.)
			const isFormElement = clickTarget.closest('input, select, textarea, button');
			if (!isFormElement) {
				event.preventDefault();
			}

			const action = actionButton.dataset.action as ReaderAction;
			const actionValue = actionButton.dataset.actionValue;
			handleAction(action, actionValue);
		});

		const isIOS =
			/iPad|iPhone|iPod/.test(navigator.userAgent) ||
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

		if (isIOS) {
			// Use touchend for iOS
			let touchStartTime = 0;
			let touchStartX = 0;
			let touchStartY = 0;

			appEl.addEventListener(
				'touchstart',
				(event) => {
					touchStartTime = Date.now();
					touchStartX = event.touches[0].clientX;
					touchStartY = event.touches[0].clientY;
				},
				{passive: true}
			);

			appEl.addEventListener('touchend', (event) => {
				const touchDuration = Date.now() - touchStartTime;
				const touch = event.changedTouches[0];
				const deltaX = Math.abs(touch.clientX - touchStartX);
				const deltaY = Math.abs(touch.clientY - touchStartY);

				// Only treat as tap if it was quick and didn't move much
				if (touchDuration < 300 && deltaX < 10 && deltaY < 10) {
					const target = event.target as HTMLElement;
					const actionButton = target.closest('[data-action]') as HTMLElement;

					// Handle action buttons first
					if (actionButton) {
						// Don't preventDefault for form elements (radio, checkbox, etc.)
						const isFormElement = target.closest('input, select, textarea, button[type="button"]');
						if (!isFormElement) {
							event.preventDefault();
						}
						event.stopImmediatePropagation();

						const action = actionButton.dataset.action as ReaderAction;
						const actionValue = actionButton.dataset.actionValue;
						handleAction(action, actionValue);
						return;
					}

					// Handle card taps
					handleTapOrClick(target, touch.clientX, touch.clientY, 'touch');
				}
			});
		} else {
			// Use click for other platforms
			articleViewEl.addEventListener('click', (event) => {
				handleTapOrClick(
					event.target as HTMLElement,
					event.clientX,
					event.clientY,
					event.pointerType as PointerType
				);
			});
		}

		function handleTapOrClick(
			target: HTMLElement,
			clientX: number,
			_clientY: number,
			pointerType: PointerType
		): boolean {
			const clickedListing = listings.get(target.closest<HTMLElement>('.article')?.dataset.id);
			const clickedCardEl = target.closest<HTMLElement>('.card') as HTMLElement;

			if (!clickedCardEl || clickedCardEl.classList.contains('card--reel')) {
				return false;
			}

			// Don't handle if it's an action button or link
			if (target.dataset.action || target.closest('a')) {
				return false;
			}

			const clickedCardNumber = Number(clickedCardEl.dataset.cardNumber);

			// Activate different listing
			if (clickedListing && clickedListing !== activeListing) {
				setActiveListing(clickedListing, clickedCardNumber || 1);
				return true;
			}

			// Activate different card in same listing
			if (activeListing && activeListing.getCurrentCardNumber() !== clickedCardNumber) {
				activeListing.activateCard(clickedCardNumber);
				activeListing.el.scrollIntoView({
					inline: inlineScroll,
					block: 'start',
					behavior: 'smooth'
				});
				updateUrl();
				return true;
			}

			// Close layout menu if clicking inside active card
			if (clickedListing && !clickedListing.layoutMenuEl.hidden) {
				clickedListing.closeLayoutMenu();
				return false;
			}

			// Tapping a card should not have any effect for non-touch modes
			if (pointerType === 'mouse') {
				return false;
			}

			const isFrontPageCard = clickedCardEl.classList.contains('front-page-card');
			const xPercent = (clientX - articleViewEl.getBoundingClientRect().left) / articleViewEl.offsetWidth;
			if (xPercent < 0.3) {
				if (isFrontPageCard) {
					scrollToToc();
				} else {
					handleAction('previous-card');
				}
			} else {
				handleAction('next-card');
			}
			return true;
		}

		document.addEventListener('keydown', (event) => {
			// We have no events for these modifiers
			if (event.metaKey || event.ctrlKey || event.shiftKey) {
				return;
			}

			if (activePageType === 'overview') {
				handleAction('toggle-overview');
				event.preventDefault();
				return;
			}

			// These actions depend on an active listing
			if (activeListing) {
				switch (event.key) {
					case 'e':
					case 'd': {
						handleStateChange(activeListing, 'done');
						event.preventDefault();
						break;
					}
					case 's':
						toggleStarred(activeListing);
						event.preventDefault();
						break;
					case 'o':
						if (activeListing.item instanceof Article && activeListing.item.layout?.uuid) {
							handleAction('open-in-lp');
						} else {
							handleAction('open-source-url');
						}
						event.preventDefault();
						break;
					case '1':
					case '2':
					case '3':
					case '4':
						toggleCardLayout(event.key);
						event.preventDefault();
						break;
					case '0':
						handleAction('set-card-layout', '');
						event.preventDefault();
						break;
				}
			}

			// These actions can also act when there's no active listing
			if (hotkeys[event.key]) {
				handleAction(hotkeys[event.key]);
				event.preventDefault();
			}
		});
	}

	function toggleCardLayout(key: string) {
		if (!activeListing) {
			return;
		}
		const cardLayouts = getLayouts(activeListing);
		if (!cardLayouts[key]) {
			return;
		}

		const currentLayout = activeListing.getActiveCardLayout();
		const nextLayout = getNext(cardLayouts[key], currentLayout);
		if (nextLayout) {
			handleAction('set-card-layout', nextLayout);
		}

		function getNext(arr: string[], value?: string) {
			if (!value) {
				return arr[0];
			}
			const index = arr.indexOf(value);
			if (index === -1) {
				return arr[0];
			}

			if (!arr[index + 1]) {
				return arr[0];
			}

			return arr[index + 1];
		}

		function getLayouts(activeListing: ReaderListing) {
			const cardNumber = activeListing.getCurrentCardNumber();
			const cardEl = activeListing.getCardEl(cardNumber);
			const cardChars = Number.parseInt(cardEl?.dataset.chars || '0', 10);
			const figureCount = Number.parseInt(cardEl?.dataset.figureCount || '0', 10);

			return getAvailableLayoutsForCard(cardNumber, mode, mobileMode.matches, cardChars, figureCount);
		}
	}

	function handleAction(action: ReaderAction, actionValue = '') {
		switch (action) {
			case 'toggle-overview':
				if (activePageType !== 'overview') {
					showOverview();
				} else {
					const [, , , , listingId, pageNumber] = window.location.pathname.split('/');
					if (listingId) {
						const listing = listings.get(listingId);
						if (listing) {
							setActiveListing(listing, Number(pageNumber) || 1);
							return;
						}
					}
					hideOverview();
					goToFrontPage();
				}
				break;
			case 'show-overview':
				showOverview();
				break;
			case 'hide-overview':
				hideOverview();
				break;
			case 'close-dialogs':
				hideOverview();
				setShowMenu('hide');
				activeListing?.closeLayoutMenu();
				break;
			case 'toggle-deep-dive':
				toggleDeepDive();
				break;
			case 'set-toc-placement':
				setTocPlacement(actionValue);
				break;
			case 'set-design':
				setDesign(actionValue);
				break;
			case 'set-mode':
				setMode(actionValue);
				activeListing?.redrawCards(mode);
				break;
			case 'set-font':
				setFont(actionValue);

				activeListing?.redrawCards(mode);
				break;
			case 'set-navigation-model':
				setNavigationModel(actionValue);
				break;
			case 'front-page':
				goToFrontPage();
				break;
			case 'show-toc':
				if (isPageNavMode()) {
					withViewTransition(() => showTocPage(true));
				} else {
					tocListEl.scrollIntoView({block: 'start', inline: 'start', behavior: 'smooth'});
				}
				break;
			case 'end-page':
				goToEndPage();
				break;
			case 'go-to-article': {
				const listingToActivate = listings.get(actionValue);
				if (!listingToActivate) {
					break;
				}
				setActiveListing(listingToActivate);
				break;
			}
			case 'next-article':
				goToNext(activeListing);
				break;
			case 'previous-article':
				goToPrevious(activeListing, false);
				break;
			case 'next-card':
				// hideNavInfo();
				if (!activeListing) {
					goToNext(null);
					break;
				}

				if (activeListing.hasNextCard()) {
					activeListing.nextCard();
					handleProgressUpdate(activeListing);
					onActivateCard(activeListing, false);
					break;
				}

				handleStateChange(activeListing, 'done');
				break;
			case 'previous-card':
				// hideNavInfo();
				if (!activeListing) {
					break;
				}

				if (activeListing.hasPreviousCard()) {
					activeListing.previousCard();
					onActivateCard(activeListing, false);
					break;
				}
				goToPrevious(activeListing, true);
				break;
			case 'activate-card':
				if (!activeListing) {
					break;
				}

				activeListing.activateCard(Number(actionValue));
				break;
			case 'done':
				activeListing && handleStateChange(activeListing, 'done');
				break;
			case 'star':
				activeListing && toggleStarred(activeListing);
				break;
			case 'toggle-show-done':
				setShowDone(actionValue);
				break;
			case 'toggle-menu':
				setShowMenu(actionValue);
				break;
			case 'reset-state':
				resetArticleState();
				break;
			case 'open-admin':
				saveEditionContext();
				window.location.href = '/admin';
				break;
			case 'toggle-layout-menu':
				activeListing?.toggleLayoutMenu();
				break;
			case 'set-card-layout':
				activeListing?.setActiveCardLayout(actionValue as CardLayout, mode);
				break;
			case 'redraw-article':
				activeListing?.redrawCards(mode);
				break;
			case 'open-in-lp':
				activeListing?.item instanceof Article &&
					window.open(
						`https://${getClientId()}.layoutpreview.aptoma.no/#!/workspace/layout/${activeListing.item.layout.uuid}/${activeListing.item.layout.version}`,
						'layoutpreview'
					);
				break;
			case 'open-source-url':
				activeListing?.item.sourceUrl && window.open(activeListing?.item.sourceUrl, 'layoutpreview');
				break;
			case 'go-to-permalink':
				if (activeListing?.item.permalink) {
					window.location.href = activeListing.item.permalink;
				}
				break;
			default:
				console.log('Unhandled action', action, actionValue);
		}
	}

	function setShowDone(value: string) {
		showDone = value || (appEl.dataset.done === 'show' ? 'hide' : 'show');
		appEl.dataset.done = showDone;
		store.setUserPreference('show-done', showDone);
	}

	function setShowMenu(value: string) {
		appEl.dataset.showMenu = value || (appEl.dataset.showMenu === 'show' ? 'hide' : 'show');
		if (appEl.dataset.showMenu === 'show') {
			if (mobileMode.matches) {
				tocListEl.scrollIntoView();
			} else {
				appMenuEl.scrollIntoView({behavior: 'smooth'});
			}
		}
	}

	function toggleDeepDive() {
		if (!activeListing) {
			return;
		}

		activeListing.toggleDeepDive();
	}

	function resetArticleState() {
		listings.forEach((listing: ReaderListing) => {
			if (listing.item.getState()) {
				store.resetItem(listing.item);
				listing.applyArticleState();
				listing.updateTocDots();
			}
		});
	}

	function saveEditionContext() {
		const context = {
			editionId: readerEdition.id,
			editionTitle: readerEdition.title,
			productId: readerEdition.productId,
			productName: readerEdition.productName,
			productTitle: readerEdition.productTitle,
			editionType: readerEdition.editionType,
			productType: readerEdition.productType,
			returnPath: readerEdition.getPath()
		};
		localStorage.setItem('editionContext', JSON.stringify(context));
	}

	function toggleStarred(activeListing: ReaderListing) {
		const articleState = activeListing.item.getState();
		const shouldBeStarred = !articleState.starred;
		store.setItemStarred(activeListing.item, shouldBeStarred);
		activeListing.applyArticleState();
		const contextId = `${readerEdition.id}-${activeListing.id}`;
		if (shouldBeStarred) {
			store.addStarredResource({
				id: contextId,
				type: 'article',
				label: activeListing.item.title,
				context: readerEdition.label,
				path: readerEdition.getArticleUrl(activeListing, 1)
			});
		} else {
			store.removeStar(contextId);
		}
	}

	function handleProgressUpdate(activeListing: ReaderListing) {
		const currentCardNumber = activeListing.getCurrentCardNumber();
		if (mobileMode.matches) {
			if (currentCardNumber > activeListing.item.mobileProgress) {
				store.setItemProgressMobile(activeListing.item, currentCardNumber);
			}
		} else {
			if (currentCardNumber > activeListing.item.desktopProgress) {
				store.setItemProgressDesktop(activeListing.item, currentCardNumber);
			}
		}
		activeListing.updateTocDots();
	}

	function handleStateChange(listing: ReaderListing, state: ItemStateValue | 'starred', toggle = false) {
		switch (state) {
			case 'starred': {
				toggleStarred(listing);
				break;
			}
			case 'done':
				if (toggle && listing.item.getState().state === 'done') {
					store.clearItemState(listing.item);
					listing.applyArticleState();
					break;
				}
				store.setItemState(listing.item, 'done');
				listing.applyArticleState();
				goToNext(listing);
				break;
			default:
				store.setItemState(listing.item, state);
				listing.applyArticleState();
				break;
		}
	}

	function goToPrevious(activeListing: ReaderListing | null, lastCard: boolean) {
		let pendingListing: ReaderListing | undefined;
		if (activeListing) {
			pendingListing = listings.getPreviousListing(activeListing, appEl.dataset.done === 'hide');
		}

		if (activePageType === 'end-page') {
			pendingListing = listings.at(-1);
		}

		if (pendingListing) {
			const pageNumber = lastCard ? pendingListing.cardCount : 1;
			setActiveListing(pendingListing, pageNumber);
		} else {
			goToFrontPage();
		}
	}

	function goToNext(activeListing: ReaderListing | null) {
		let pendingListing: ReaderListing | undefined;
		if (activeListing) {
			pendingListing = listings.getNextListing(activeListing, appEl.dataset.done === 'hide');
		} else if (activePageType === 'front-page') {
			pendingListing = listings.at(0);
		}

		if (pendingListing) {
			setActiveListing(pendingListing);
		} else {
			goToEndPage();
		}
	}

	function markListingAsNotActive(listing: ReaderListing | null) {
		if (!listing) {
			return;
		}

		listing.markAsNotActive();

		if (!(mobileMode.matches || tabletMode.matches)) {
			removeListing(listing);
		}
	}

	function setActiveListing(listing: ReaderListing, pageNumber = 1, skipScroll = false, skipUpdateUrl = false) {
		if (activePageType === 'overview') {
			hideOverview();
		}

		const wasNotInArticleView = activePageType && activePageType !== 'article';

		if (!activeListing) {
			if (!(mobileMode.matches || tabletMode.matches)) {
				hideFrontPage();
				hideEndPage();
			}
		}

		if (activeTocListing && activeTocListing !== listing) {
			activeTocListing?.tocEntryEl.classList.remove('toc-entry--active');
		}

		if (listing !== activeListing) {
			markListingAsNotActive(activeListing);
		}
		activeListing = listing;

		// Page navigation mode: show article view when transitioning from TOC
		if (isPageNavMode() && wasNotInArticleView) {
			withViewTransition(() => {
				// TODO: This causes active page type to be set late, we should maybe handle this differently
				// Set page type inside transition so view-transition-names are correct
				setActivePageType('article');

				// Hide TOC, show article view
				tocListEl.style.display = 'none';
				articleViewEl.style.display = '';

				// Call currentArticleMode after article view is visible
				listing.currentArticleMode(mode);

				// Scroll to the article immediately after showing article view
				if ((mobileMode.matches || tabletMode.matches) && !skipScroll) {
					activeListing?.el.scrollIntoView({block: 'start', behavior: 'instant'});
				}
			});
		} else {
			setActivePageType('article');
			listing.currentArticleMode(mode);
			if (!(mobileMode.matches || tabletMode.matches)) {
				triggerAnimation(listing.el, FADE_IN);
			}
		}

		listing.activateCard(pageNumber, skipScroll);
		onActivateCard(listing, skipScroll, skipUpdateUrl);
		handleProgressUpdate(activeListing);

		if (isPageNavMode() && wasNotInArticleView) {
			return;
		}

		// Scroll to article
		if ((mobileMode.matches || tabletMode.matches) && !skipScroll) {
			isScrollingIntoView = true;
			activeListing.el.scrollIntoView({block: 'start', inline: inlineScroll, behavior: 'smooth'});
		}

		if (
			(mobileMode.matches && !isPageNavMode()) ||
			tabletMode.matches ||
			(wideScreenMode.matches && ['left', 'right'].includes(tocPlacement))
		) {
			const tocEntriesEl = tocListEl.querySelector('.toc__entries') as HTMLElement;
			const entryRect = activeListing.tocEntryEl.getBoundingClientRect();
			const containerRect = tocEntriesEl.getBoundingClientRect();
			if (entryRect.bottom > containerRect.bottom || entryRect.top < containerRect.top) {
				tocEntriesEl.scrollTo({
					top: activeListing.tocEntryEl.offsetTop - tocEntriesEl.offsetHeight / 2,
					behavior: 'smooth'
				});
			}
		}
	}

	function setTocPlacement(placement: string) {
		tocPlacement = placement;
		appEl.dataset.tocPlacement = tocPlacement;
		store.setUserPreference('toc-placement', tocPlacement);
	}

	function setDesign(style: string) {
		let appliedStyle = style;
		if (!style) {
			appliedStyle = design === 'gunnar' ? 'geir' : 'gunnar';
		}
		design = appliedStyle;
		appEl.dataset.design = design;
		store.setUserPreference('design', design);
	}

	function setMode(modeName: string) {
		let appliedModeName = modeName;
		if (!modeName) {
			appliedModeName = mode === 'default' ? 'pressflow' : 'default';
		}

		mode = appliedModeName;
		appEl.dataset.mode = mode;
		store.setUserPreference('mode', mode);
	}

	function setFont(fontName: string) {
		let appliedFontName = fontName;
		if (!fontName) {
			// Cycle through font options: serif -> sans-serif -> customer-serif -> customer-sans-serif -> serif
			switch (font) {
				case 'serif':
					appliedFontName = 'sans-serif';
					break;
				case 'sans-serif':
					appliedFontName = 'customer-serif';
					break;
				case 'customer-serif':
					appliedFontName = 'customer-sans-serif';
					break;
				case 'customer-sans-serif':
					appliedFontName = 'serif';
					break;
				default:
					appliedFontName = 'serif';
			}
		}

		font = appliedFontName;
		appEl.dataset.font = font;
		store.setUserPreference('font', font);
	}

	function setNavigationModel(model: string) {
		if (!model || (model !== 'horizontal-scroll' && model !== 'page-navigation')) {
			return;
		}

		navigationModel = model;
		appEl.dataset.navigationModel = navigationModel;
		store.setUserPreference('navigation-model', navigationModel);

		// Reload page to properly apply new navigation model
		window.location.reload();
	}

	function withViewTransition(updateCallback: () => void | Promise<void>) {
		// Use View Transitions API if supported and in page-navigation mode
		if (!document.startViewTransition || !isPageNavMode()) {
			updateCallback();
			return;
		}

		document.startViewTransition(() => {
			const result = updateCallback();
			return result instanceof Promise ? result : Promise.resolve();
		});
	}

	function showTocPage(addHistoryEntry = false) {
		if (!isPageNavMode()) {
			return;
		}

		// Hide article view, show TOC
		articleViewEl.style.display = 'none';
		tocListEl.style.display = 'flex';

		// Clear active listing
		markListingAsNotActive(activeListing);
		activeTocListing = activeListing;
		activeListing = null;
		activeTocListing?.tocEntryEl.classList.add('toc-entry--active');
		setActivePageType('toc');

		// Update URL if needed
		const newUrl = readerEdition.getPath() + window.location.search;
		if (addHistoryEntry && window.location.href !== newUrl) {
			history.pushState({pageType: 'toc', id: null}, '', newUrl);
		}
	}

	// function showNavInfo() {
	// 	appEl.classList.add('app--show-nav-info');
	// 	appEl.style.setProperty('--nav-info-offset', '0%');
	// }
	//
	// function hideNavInfo() {
	// 	appEl.style.setProperty('--nav-info-offset', '100%');
	// 	appEl.classList.remove('app--show-nav-info');
	// }

	function showOverview() {
		setActivePageType('overview');
		updateUrl();

		overViewEl.addEventListener('click', hideOverview);

		listings.forEach((listing: ReaderListing) => {
			overViewEl.appendChild(listing.el);
			listing.activateCard(1);
			listing.postProcessCards(mode);
		});

		if (activeListing) {
			activeListing.el.scrollIntoView({block: 'center'});
		}
		activeListing = null;
	}

	function hideOverview() {
		setActivePageType(activeListing ? 'article' : 'front-page');
		overViewEl.innerHTML = '';
		overViewEl.removeEventListener('click', hideOverview);
	}

	function removeListing(listing: ReaderListing) {
		triggerAnimation(listing.el, FADE_OUT, () => listing.removeFromCurrentViewEl());
	}

	function goToFrontPage(skipUpdateUrl = false) {
		if (resourceType === 'article') {
			return;
		}

		if (mobileMode.matches || tabletMode.matches) {
			goToMobileFrontPage();
		} else {
			goToDesktopFrontPage();
		}

		activeListing = null;
		setActivePageType('front-page');
		setShowMenu('hide');
		if (!skipUpdateUrl) {
			updateUrl();
		}
	}

	function activateFrontPage() {
		markListingAsNotActive(activeListing);
		activeListing = null;
		setActivePageType('front-page');
		updateUrl();
	}

	function setActivePageType(value: PageType) {
		activePageType = value;
		appEl.dataset.pageType = activePageType;
	}

	function goToMobileFrontPage() {
		markListingAsNotActive(activeListing);
		frontPageEl.scrollIntoView({
			block: 'start',
			inline: inlineScroll,
			behavior: 'smooth'
		});
	}

	function scrollToToc() {
		tocListEl.scrollIntoView({
			block: 'start',
			inline: inlineScroll,
			behavior: 'smooth'
		});
	}

	function goToDesktopFrontPage() {
		markListingAsNotActive(activeListing);
		if (activePageType === 'end-page') {
			hideEndPage();
		}
		articleViewEl.appendChild(frontPageEl);
		articleViewEl.classList.add('article-view__front-page');
	}

	function hideFrontPage() {
		if (frontPageEl && frontPageEl.parentNode === articleViewEl) {
			articleViewEl.removeChild(frontPageEl);
			articleViewEl.classList.remove('article-view__front-page');
		}
	}

	function goToEndPage(scrollBehavior: 'smooth' | 'instant' = 'smooth') {
		if (resourceType === 'article') {
			return;
		}

		markListingAsNotActive(activeListing);
		activeListing = null;
		if (activePageType === 'front-page') {
			hideFrontPage();
		}
		if (activePageType !== 'end-page') {
			hideFrontPage();
			setActivePageType('end-page');
			articleViewEl.classList.add('article-view__end-page');
			updateUrl();
		}

		if (mobileMode.matches || tabletMode.matches) {
			if (endPageEl.parentNode !== articleViewEl) {
				articleViewEl.appendChild(endPageEl);
			}
			isScrollingIntoView = true;
			articleViewEl.querySelector('.end-page-card')?.scrollIntoView({block: 'end', behavior: scrollBehavior});
		} else {
			articleViewEl.appendChild(endPageEl);
			articleViewEl.scrollIntoView({block: 'start', behavior: 'smooth'});
		}
	}

	function hideEndPage() {
		if (endPageEl && endPageEl.parentNode === articleViewEl) {
			articleViewEl.removeChild(endPageEl);
			articleViewEl.classList.remove('article-view__end-page');
		}
	}

	function onActivateCard(listing: ReaderListing, skipScroll: boolean, skipUpdateUrl = false) {
		appEl.classList.remove('app--show-nav-info');
		if (activePageType === 'overview') {
			setActiveListing(listing, listing.getCurrentCardNumber());
		}
		if (activeListing !== listing) {
			setActiveListing(listing, listing.getCurrentCardNumber());
			return;
		}
		if (!skipUpdateUrl) {
			updateUrl('article');
		}
		if (!skipScroll) {
			listing.el.scrollIntoView({
				block: 'start',
				inline: inlineScroll,
				behavior: 'smooth'
			});
		}
	}

	function createListings(items: ReaderItem[]) {
		const listings = new Listings();

		items.forEach((item, index: number) => {
			const listing = new ReaderListing(
				item,
				readerEdition,
				index + 1,
				store,
				articleViewEl,
				setActiveListing,
				handleStateChange,
				mobileMode
			);
			listings.add(listing);
		});

		listings.sort(reverseSortOrder);
		listings.updateLinkedList();

		return listings;
	}

	function renderItems(listings: Listings | ReaderListing[]) {
		listings.forEach((listing: ReaderListing) => {
			loadItemPromises.push(listing.loadAndRender());
		});
	}
}

async function ensureFontsLoaded(element: HTMLElement): Promise<void> {
	await document.fonts.ready;

	// Get computed styles from the app element to capture customer-specific fonts
	const elementStyle = getComputedStyle(element);
	const sansFontStack = elementStyle.getPropertyValue('--sans-serif-font-stack').trim();
	const serifFontStack = elementStyle.getPropertyValue('--serif-font-stack').trim();

	// Extract only the first font from each stack (primary font)
	const fontsToLoad = new Set<string>();
	for (const stack of [sansFontStack, serifFontStack]) {
		if (!stack) {
			continue;
		}

		// Get the first font in the stack (before the first comma)
		const firstFont = stack.split(',')[0].trim().replace(/['"]/g, '');
		if (firstFont) {
			fontsToLoad.add(firstFont);
		}
	}

	// Load each font with common weights and styles used in articles
	const weights = ['400', '700'];
	const styles = ['normal', 'italic'];
	const loadPromises: Promise<FontFace[]>[] = [];

	for (const fontFamily of fontsToLoad) {
		for (const weight of weights) {
			for (const style of styles) {
				try {
					// Check if font is already loaded
					const fontSpec = `${style} ${weight} 16px "${fontFamily}"`;
					const loaded = document.fonts.check(fontSpec);
					if (!loaded) {
						// Force load by creating temporary element
						const span = document.createElement('span');
						span.style.fontFamily = `"${fontFamily}"`;
						span.style.fontWeight = weight;
						span.style.fontStyle = style;
						span.style.position = 'absolute';
						span.style.visibility = 'hidden';
						span.textContent = 'Font loading test';
						document.body.appendChild(span);

						// Load the font
						loadPromises.push(document.fonts.load(fontSpec));

						// Clean up
						setTimeout(() => span.remove(), 0);
					}
				} catch (e) {
					// Font might not exist, continue
					console.warn(`Could not load font: ${fontFamily} ${weight} ${style}`, e);
				}
			}
		}
	}

	// Wait for all fonts to load
	if (loadPromises.length > 0) {
		await Promise.all(loadPromises);
	}
}
